import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import PublicShell from './PublicShell';
import Icon from '../../comps/Icon';
import publicBusinessSvc from '../../services/publicBusiness';
import { useI18n, formatDate } from '../../i18n/I18nContext';
import '../../style/css/public2026.css';

/*
 * ManageReservation — the PUBLIC (no-auth) customer self-manage screen at /r/:token. The token
 * is the ONLY thing the client holds; the server scopes strictly to that one reservation and
 * never exposes any other. For now: VIEW + CANCEL (edit/change is a later step).
 */
export default function ManageReservation() {
    const { t, dateLocale } = useI18n();
    const { token } = useParams();
    const [state, setState] = useState('loading'); // loading | ready | notfound | error
    const [res, setRes] = useState(null);
    const [cancelling, setCancelling] = useState(false);
    const [cancelErr, setCancelErr] = useState('');

    const load = useCallback(async () => {
        setState('loading');
        const data = await publicBusinessSvc.getReservationByToken(token);
        if (data && data.date) { setRes(data); setState('ready'); }
        else { setState('notfound'); }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const isCancelled = res && (res.liveStatus === 'cancelled' || res.status === 3);

    const doCancel = async () => {
        if (cancelling || isCancelled) return;
        // eslint-disable-next-line no-restricted-globals, no-alert
        if (!window.confirm(t('manage.confirm_cancel'))) return;
        setCancelling(true); setCancelErr('');
        const out = await publicBusinessSvc.cancelReservationByToken(token);
        setCancelling(false);
        if (out.ok) { setRes((r) => ({ ...r, liveStatus: 'cancelled', status: 3 })); }
        else { setCancelErr(t('manage.cancel_failed')); }
    };

    if (state === 'loading') {
        return <PublicShell><div className="pb" style={{ padding: 40, textAlign: 'center' }}>{t('manage.loading')}</div></PublicShell>;
    }

    if (state === 'notfound' || state === 'error' || !res) {
        return (
            <PublicShell>
                <div className="mr-card mr-card--msg">
                    <h2 className="mr-title">{t('manage.invalid_title')}</h2>
                    <p className="mr-sub">{t('manage.invalid_sub')}</p>
                    <Link to="/discover" className="pb-btn-ghost pb-btn-link">{t('common.back_to_discovery')}</Link>
                </div>
            </PublicShell>
        );
    }

    return (
        <PublicShell>
            <div className="mr-card">
                <div className="mr-biz">{res.businessName || t('manage.default_biz')}</div>
                <h2 className="mr-title">{t('manage.title')}</h2>

                <div className={`mr-chip ${isCancelled ? 'mr-chip--cancelled' : 'mr-chip--ok'}`}>
                    {isCancelled ? t('manage.status_cancelled') : t('manage.status_confirmed')}
                </div>

                <div className="mr-details">
                    <div className="mr-row"><span className="mr-ico" aria-hidden="true"><Icon name="customer" size={18} /></span><span>{res.guestName}</span></div>
                    <div className="mr-row"><span className="mr-ico" aria-hidden="true"><Icon name="calendar" size={18} /></span><span>{formatDate(res.date, dateLocale)}</span></div>
                    <div className="mr-row"><span className="mr-ico" aria-hidden="true"><Icon name="clock" size={18} /></span><span>{String(res.startTime).slice(0, 5)}</span></div>
                    <div className="mr-row"><span className="mr-ico" aria-hidden="true"><Icon name="users" size={18} /></span><span>{res.numberOfGuests} {t('manage.diners')}</span></div>
                </div>

                {isCancelled ? (
                    <p className="mr-cancelled-note">{t('manage.cancelled_note')}</p>
                ) : (
                    <>
                        {cancelErr && <p className="mr-err">{cancelErr}</p>}
                        <button type="button" className="mr-cancel-btn" onClick={doCancel} disabled={cancelling}>
                            {cancelling ? t('manage.cancelling') : t('manage.cancel_btn')}
                        </button>
                        <p className="mr-note">{t('manage.reschedule_note')}</p>
                    </>
                )}

                {res.businessSlug && (
                    <Link to={`/b/${res.businessSlug}`} className="pb-btn-ghost pb-btn-link">{t('manage.to_business_page')}</Link>
                )}
            </div>
        </PublicShell>
    );
}
